import React, {
	createContext,
	useMemo,
	useRef,
	useState,
	type PropsWithChildren,
	type MutableRefObject,
	useCallback,
} from 'react';
import noop from 'lodash/noop';
import type { PluggableItemsResponse } from '../../common/types.tsx';

type PluggableItemsContextType = {
	pluggableItems: PluggableItemsResponse;
	setPluggableItems: (value: PluggableItemsResponse) => void;
	lastKeyRef: MutableRefObject<string | undefined>;
};

const initialResponse: PluggableItemsResponse = {
	data: undefined,
	loading: false,
	error: false,
};

export const PluggableItemsCacheContext = createContext<PluggableItemsContextType>({
	pluggableItems: initialResponse,
	setPluggableItems: noop,
	lastKeyRef: { current: undefined },
});

export const PluggableItemsCacheProvider = ({
	onDataChanged,
	children,
}: PropsWithChildren<{
	onDataChanged: () => void;
}>): React.JSX.Element => {
	const [pluggableItems, setPluggableItemsBase] = useState<PluggableItemsResponse>(initialResponse);
	const lastKeyRef = useRef<string | undefined>(undefined);

	const setPluggableItems = useCallback(
		(newPluggableItems: PluggableItemsResponse) => {
			setPluggableItemsBase(newPluggableItems);
			onDataChanged();
		},
		[onDataChanged],
	);

	const contextValue = useMemo<PluggableItemsContextType>(
		() => ({
			pluggableItems,
			setPluggableItems,
			lastKeyRef,
		}),
		[pluggableItems, setPluggableItems, lastKeyRef],
	);

	return (
		<PluggableItemsCacheContext.Provider value={contextValue}>
			{children}
		</PluggableItemsCacheContext.Provider>
	);
};
